import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Store, Package, Users, ShoppingCart,
  ClipboardList, History, Globe, Wallet, LogOut, Menu, ArrowLeft,
  Crown, Building2, UserCog, KeyRound, Wrench, Sun, Moon, CalendarClock, Target, PlayCircle, CalendarRange, Bell, Sparkles, Smartphone, Calculator, BadgeDollarSign
} from "lucide-react";
import { BRAND } from "@/components/Brand";
import { useTheme } from "@/ThemeContext";
import InstallPWAHeaderButton from "@/components/InstallPWAButton";
import NotificationCenter from "@/components/NotificationCenter";

function buildNav({ isMaster, isImpersonating, isOwner, role, canSeeFinance, tutorialLabel, tutorialEnabled }) {
  // Master without impersonating: only Master tab
  if (isMaster && !isImpersonating) {
    return [
      { to: "/master", label: "Master Lojistas", icon: Crown, end: true },
      { to: "/master/notificacoes", label: "Notificações", icon: Bell },
      { to: "/master/crm", label: "CRM Master", icon: Target },
      { to: "/master/tutoriais", label: "Tutoriais (admin)", icon: PlayCircle },
      { to: "/master/avaliador", label: "Avaliador de iPhone", icon: Smartphone },
      { to: "/conta", label: "Minha conta", icon: KeyRound },
    ];
  }
  // Tecnico: only assistance + own account
  if (role === "tecnico") {
    return [
      { to: "/assistencia", label: "Assistência", icon: Wrench, end: true },
      ...(tutorialEnabled ? [{ to: "/tutoriais", label: tutorialLabel || "Como Usar", icon: PlayCircle }] : []),
      { to: "/conta", label: "Minha conta", icon: KeyRound },
    ];
  }
  // Marketing CRM: only CRM + tutoriais + minha conta
  if (role === "marketing_crm") {
    return [
      { to: "/crm", label: "CRM", icon: Target, end: true },
      ...(tutorialEnabled ? [{ to: "/tutoriais", label: tutorialLabel || "Como Usar", icon: PlayCircle }] : []),
      { to: "/conta", label: "Minha conta", icon: KeyRound },
    ];
  }
  // Tenant context (owner / employees / master impersonating)
  const base = [
    { to: "/", label: "Painel", icon: LayoutDashboard, end: true },
    { to: "/loja", label: "Dados da Loja", icon: Store },
    { to: "/produtos", label: "Produtos", icon: Package },
    { to: "/clientes", label: "Clientes", icon: Users },
    { to: "/crediario", label: "Crediário", icon: CalendarRange },
    { to: "/crm", label: "CRM", icon: Target },
    { to: "/vendas", label: "Vender", icon: ShoppingCart },
    { to: "/simulador-taxas", label: "Simulador de Taxas", icon: Calculator },
    { to: "/pedidos", label: "Pedidos", icon: ClipboardList },
    { to: "/historico", label: "Histórico de vendas", icon: History },
    { to: "/assistencia", label: "Assistência", icon: Wrench },
    { to: "/comparador", label: "Comparador (Em teste)", icon: Sparkles },
    { to: "/avaliador", label: "Avaliador de iPhone", icon: Smartphone },
    { to: "/catalogo-admin", label: "Catálogo / Site", icon: Globe },
  ];
  if (canSeeFinance) base.push({ to: "/financeiro", label: "Financeiro", icon: Wallet });
  if (canSeeFinance) base.push({ to: "/comissoes", label: "Comissões", icon: BadgeDollarSign });
  if (isOwner) base.push({ to: "/funcionarios", label: "Acessos de colaboradores", icon: UserCog });
  if (tutorialEnabled) base.push({ to: "/tutoriais", label: tutorialLabel || "Como Usar", icon: PlayCircle, highlight: true });
  base.push({ to: "/conta", label: "Minha conta", icon: KeyRound });
  // "Meu plano" — apenas para quem tem loja (master impersonando, owner,
  // funcionários). Master sem impersonate já retornou antes (linha ~16).
  base.push({ to: "/meu-plano", label: "Meu plano", icon: CalendarClock });
  return base;
}

function NavList({ items, onSelect }) {
  return (
    <nav className="p-3 flex-1 overflow-y-auto">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onSelect}
          data-testid={`nav-${label.toLowerCase().replace(/\s|\//g,'-')}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
              isActive ? "bg-[#0A0A0A] text-white" : "text-neutral-700 hover:bg-neutral-100"
            }`
          }>
          <Icon size={16} className="flex-shrink-0" />
          <span className="truncate">{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const auth = useAuth();
  const { user, logout, isMaster, isImpersonating, isOwner, role, canSeeFinance, exitImpersonation } = auth;
  const { theme, toggle: toggleTheme, isDark } = useTheme();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tutorialCfg, setTutorialCfg] = useState({ menu_label: "Como Usar", enabled: true });
  // Carrega a config do menu de tutoriais (label + enabled) sem auth — endpoint público
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/tutorial-settings`)
      .then(r => r.json())
      .then(d => { if (!cancelled) setTutorialCfg(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const isRoot = location.pathname === "/" || location.pathname === "/master";
  const items = buildNav({ isMaster, isImpersonating, isOwner, role, canSeeFinance,
                          tutorialLabel: tutorialCfg.menu_label, tutorialEnabled: tutorialCfg.enabled !== false });
  const currentNav = items.find(n => n.end ? location.pathname === n.to : location.pathname.startsWith(n.to) && n.to !== "/");
  const pageTitle = currentNav?.label || "Painel";

  const handleLogout = async () => { await logout(); nav("/login"); };
  const handleExit = async () => { await exitImpersonation(); nav("/master"); };

  const tenantName = user?.impersonated_tenant?.name || user?.tenant?.name;

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-[#E5E5E5] sticky top-0 h-screen">
        <Link to={isMaster && !isImpersonating ? "/master" : "/"} className="p-5 border-b border-[#E5E5E5] flex items-center gap-3 hover:bg-neutral-50 transition">
          <img src={BRAND.logo} alt={BRAND.name} className="w-10 h-10 object-contain rounded-md flex-shrink-0"/>
          <div className="min-w-0">
            <div className="font-display font-bold tracking-tight leading-none truncate">
              {isMaster && !isImpersonating ? (
                <><span>Gestão </span><span style={{ background: "linear-gradient(90deg, #4F46E5, #A855F7)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Expert</span></>
              ) : (tenantName || <><span>Gestão </span><span style={{ background: "linear-gradient(90deg, #4F46E5, #A855F7)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Expert</span></>)}
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-400 mt-1 truncate">
              {role === "master" && !isImpersonating ? "Administrador" : role === "owner" ? "Lojista" : role === "gerente" ? "Gerente" : role === "colaborador" ? "Colaborador" : role === "tecnico" ? "Técnico" : role === "marketing_crm" ? "Marketing CRM" : ""}
            </div>
          </div>
        </Link>
        <NavList items={items} />
        <div className="p-3 pwa-safe-bottom border-t border-[#E5E5E5]">
          <div className="px-3 pb-3">
            <div className="text-xs text-neutral-500">Logado como</div>
            <div className="text-sm font-medium truncate">{user?.email}</div>
          </div>
          <Button variant="outline" className="w-full justify-start gap-2" onClick={handleLogout} data-testid="logout-button">
            <LogOut size={16} /> Sair
          </Button>
          <Button variant="ghost" className="w-full justify-start gap-2 mt-2 text-neutral-600" onClick={toggleTheme} data-testid="theme-toggle-button">
            {isDark ? <Sun size={16}/> : <Moon size={16}/>} {isDark ? "Modo claro" : "Modo noturno"}
          </Button>
          <div className="text-[9px] text-neutral-400 text-center mt-3 tracking-[0.15em] uppercase">{BRAND.tagline}</div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Impersonation banner */}
        {isImpersonating && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 pwa-safe-top flex items-center justify-between gap-3 text-sm sticky top-0 z-40 md:static">
            <div className="flex items-center gap-2 min-w-0">
              <Building2 size={14} className="text-amber-600 flex-shrink-0"/>
              <span className="truncate">Modo Master — visualizando: <strong>{tenantName}</strong></span>
            </div>
            <Button size="sm" variant="outline" onClick={handleExit} className="border-amber-300" data-testid="exit-impersonation-button">
              Sair da loja
            </Button>
          </div>
        )}

        {/* Mobile header */}
        <header className={`md:hidden sticky ${isImpersonating ? "top-0" : "top-0"} z-30 flex items-center justify-between px-3 py-3 ${isImpersonating ? "" : "pwa-safe-top"} border-b border-[#E5E5E5] bg-white/90 backdrop-blur-xl`}>
          <div className="flex items-center gap-1">
            {!isRoot && (
              <Button size="sm" variant="ghost" onClick={() => nav(isMaster && !isImpersonating ? "/master" : "/")} data-testid="mobile-back-button" className="gap-1">
                <ArrowLeft size={18} />
                <span className="text-sm">Voltar</span>
              </Button>
            )}
            {isRoot && <InstallPWAHeaderButton />}
          </div>
          <div className="font-display font-semibold tracking-tight truncate max-w-[50%]">{pageTitle}</div>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button size="icon" variant="ghost" data-testid="mobile-menu-button"><Menu size={20} /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0 flex flex-col">
              <div className="p-6 pwa-safe-top border-b border-[#E5E5E5] flex items-center gap-3">
                <div className="w-9 h-9 rounded-md bg-[#0A0A0A] text-white flex items-center justify-center">
                  {isMaster && !isImpersonating ? <Crown size={18} /> : <Store size={18} />}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold tracking-tight leading-none truncate">
                    {isMaster && !isImpersonating ? (
                      <><span>Gestão </span><span style={{ background: "linear-gradient(90deg, #4F46E5, #A855F7)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Expert</span></>
                    ) : (tenantName || <><span>Gestão </span><span style={{ background: "linear-gradient(90deg, #4F46E5, #A855F7)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Expert</span></>)}
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-400 mt-1">{role}</div>
                </div>
              </div>
              <NavList items={items} onSelect={() => setMobileOpen(false)} />
              <div className="p-3 border-t border-[#E5E5E5]">
                <div className="px-3 pb-3">
                  <div className="text-xs text-neutral-500">Logado como</div>
                  <div className="text-sm font-medium truncate">{user?.email}</div>
                </div>
                <Button variant="outline" className="w-full justify-start gap-2" onClick={handleLogout}>
                  <LogOut size={16} /> Sair
                </Button>
                <Button variant="ghost" className="w-full justify-start gap-2 mt-2 text-neutral-600" onClick={toggleTheme}>
                  {isDark ? <Sun size={16}/> : <Moon size={16}/>} {isDark ? "Modo claro" : "Modo noturno"}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </header>

        {!isRoot && (
          <div className="hidden md:block max-w-7xl mx-auto pt-6 px-6 lg:px-10">
            <button onClick={() => nav(isMaster && !isImpersonating ? "/master" : "/")} className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-[#0A0A0A] transition" data-testid="desktop-back-button">
              <ArrowLeft size={14}/> Voltar
            </button>
          </div>
        )}

        <div className="p-6 lg:p-10 max-w-7xl mx-auto">
          <Outlet />
        </div>
        <NotificationCenter />
      </main>
    </div>
  );
}
